/**
 * UA Dashboard — Step 4 (top of the 4-level Scorecard hierarchy).
 *
 * Macro view of the whole company:
 *   • Company-wide rollup (combined achievement % across every PM/IB/Staff)
 *   • Per-division cards (one per PM org node — Introducing Broker, SaaS, etc.)
 *   • SaaS product placeholders (5 cards, admin-editable)
 *   • Sembang Oh Yeah card (community/platform mention)
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { SectionHeader } from '@/components/SectionHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Building2, RefreshCw, AlertCircle, Edit2, Sparkles,
  TrendingUp, ChevronRight, Package, MessageCircle, Briefcase,
} from 'lucide-react';
import {
  OverallSummaryCard, StatusBadge, ProgressBar, PeriodSelector, Sparkline, StatusDonut,
} from '@/components/scorecard/ScorecardPrimitives';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DivisionCard({ division, trend, onClick }) {
  return (
    <div
      onClick={onClick}
      className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-5 hover:border-[hsl(var(--border-hover))] hover:-translate-y-px transition-all cursor-pointer group"
      data-testid={`division-card-${division.pm_node_id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Briefcase size={14} className="text-[#9CA3AF]" />
            <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Division</p>
          </div>
          <p className="text-white text-base font-semibold truncate">{division.name}</p>
          {division.description && (
            <p className="text-[10px] text-[#6B7280] mt-0.5 truncate">{division.description}</p>
          )}
        </div>
        <ChevronRight size={16} className="text-[#6B7280] group-hover:text-white transition flex-shrink-0" />
      </div>
      <div className="flex items-end justify-between mb-2 gap-3">
        <p className="text-3xl font-bold text-white">
          {Math.min(division.overall.overall_pct, 100).toFixed(1)}<span className="text-sm text-[#6B7280]">%</span>
        </p>
        <StatusBadge status={division.overall.status} />
      </div>
      {trend && trend.length > 0 && (
        <div className="mt-1 mb-2 -mx-1">
          <Sparkline data={trend} width={220} height={28} />
          <div className="flex justify-between text-[9px] text-[#6B7280] mt-0.5 px-0.5">
            <span>{trend[0]?.period}</span>
            <span>last 6 months</span>
            <span>{trend[trend.length - 1]?.period}</span>
          </div>
        </div>
      )}
      <ProgressBar pct={division.overall.overall_pct} status={division.overall.status} />
      {division.revenue_total !== undefined && (
        <div className="mt-3 flex items-center justify-between bg-gradient-to-r from-[#10B981]/10 to-transparent border border-[#10B981]/20 rounded-md px-3 py-2">
          <span className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Revenue</span>
          <span className="text-sm font-bold text-white tabular-nums">
            <span className="text-[#10B981] mr-0.5">RM</span>
            {Number(division.revenue_total || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </span>
        </div>
      )}
      <div className="grid grid-cols-3 gap-2 mt-3 text-center">
        <div className="bg-[hsl(var(--surface-elevated))] rounded-md py-1.5">
          <p className="text-base font-bold text-white">{division.child_ib_count}</p>
          <p className="text-[9px] text-[#6B7280] uppercase">Channels</p>
        </div>
        <div className="bg-[hsl(var(--surface-elevated))] rounded-md py-1.5">
          <p className="text-base font-bold text-white">{division.child_staff_count}</p>
          <p className="text-[9px] text-[#6B7280] uppercase">Team</p>
        </div>
        <div className="bg-[hsl(var(--surface-elevated))] rounded-md py-1.5">
          <p className="text-base font-bold text-[#10B981]">{division.overall.on_track_count}</p>
          <p className="text-[9px] text-[#6B7280] uppercase">On Track</p>
        </div>
      </div>
    </div>
  );
}

function SaaSCard({ product, onEdit }) {
  const target = product.target_monthly_myr || 0;
  const current = product.current_mtd_myr || 0;
  const pct = target > 0 ? (current / target) * 100 : 0;
  const color = product.color || '#3B82F6';
  return (
    <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4 hover:border-[hsl(var(--border-hover))] transition group" data-testid={`saas-card-${product.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}20` }}>
            <Package size={16} style={{ color }} />
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-semibold truncate">{product.name}</p>
            <p className="text-[10px] text-[#6B7280]">SaaS Product</p>
          </div>
        </div>
        <button onClick={() => onEdit(product)} className="opacity-0 group-hover:opacity-100 transition" data-testid={`saas-edit-${product.id}`}>
          <Edit2 size={13} className="text-[#9CA3AF] hover:text-white" />
        </button>
      </div>
      <p className="text-2xl font-bold text-white">RM {current.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
      <div className="flex items-center gap-2 mt-1 text-[10px] text-[#6B7280]">
        <span>Target: RM {target.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
        <span className="text-[#10B981]">· {product.active_users || 0} users</span>
      </div>
      <div className="mt-2 w-full bg-[hsl(var(--surface-elevated))] rounded-full h-1 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }} />
      </div>
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[10px] text-[#9CA3AF]">{pct.toFixed(0)}% of target</span>
        {product.notes && <span className="text-[9px] text-[#6B7280] italic truncate ml-2 max-w-[60%]">{product.notes}</span>}
      </div>
    </div>
  );
}

function SaaSEditModal({ product, isOpen, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (product) setForm({
      name: product.name || '',
      target_monthly_myr: product.target_monthly_myr || 0,
      current_mtd_myr: product.current_mtd_myr || 0,
      active_users: product.active_users || 0,
      notes: product.notes || '',
    });
  }, [product]);

  const save = async () => {
    if (!product?.id) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/scorecard/saas-products/${product.id}`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('SaaS product updated');
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error('Save gagal: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] max-w-md" data-testid="saas-edit-modal">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Package size={18} />Edit {product?.name || 'Product'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-3">
          <div>
            <label className="text-xs text-[#9CA3AF] mb-1 block">Product Name</label>
            <Input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-transparent border-[hsl(var(--border))] text-white" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-[#9CA3AF] mb-1 block">Target (MYR/month)</label>
              <Input type="number" value={form.target_monthly_myr || 0} onChange={(e) => setForm({ ...form, target_monthly_myr: Number(e.target.value) })} className="bg-transparent border-[hsl(var(--border))] text-white" />
            </div>
            <div>
              <label className="text-xs text-[#9CA3AF] mb-1 block">Current MTD (MYR)</label>
              <Input type="number" value={form.current_mtd_myr || 0} onChange={(e) => setForm({ ...form, current_mtd_myr: Number(e.target.value) })} className="bg-transparent border-[hsl(var(--border))] text-white" />
            </div>
          </div>
          <div>
            <label className="text-xs text-[#9CA3AF] mb-1 block">Active Users</label>
            <Input type="number" value={form.active_users || 0} onChange={(e) => setForm({ ...form, active_users: Number(e.target.value) })} className="bg-transparent border-[hsl(var(--border))] text-white" />
          </div>
          <div>
            <label className="text-xs text-[#9CA3AF] mb-1 block">Notes (optional)</label>
            <Input value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. waiting on Stripe integration" className="bg-transparent border-[hsl(var(--border))] text-white" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-gradient-to-r from-[#10B981] to-[#3B82F6] text-white" data-testid="saas-edit-save-btn">
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function UADashboardPage() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoSwitched, setAutoSwitched] = useState(false);
  const [editingSaas, setEditingSaas] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const fetchOverview = async (p = period) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [r, tr] = await Promise.all([
        axios.get(`${API}/scorecard/ua-overview?period=${p}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/scorecard/trend?scope=ib_management&months=6&period_end=${p}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setData(r.data);
      setTrend(tr.data?.trend || []);
      // Auto-switch period
      if (!autoSwitched && r.data?.success && r.data.company?.overall?.status === 'no_data') {
        const available = r.data.available_periods || [];
        if (available.length > 0 && available[0] !== p) {
          setAutoSwitched(true);
          setPeriod(available[0]);
        }
      }
    } catch (e) {
      toast.error('Gagal load UA overview: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOverview(period); /* eslint-disable-next-line */ }, [period]);

  const seedSaas = async () => {
    setSeeding(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/scorecard/saas-products/seed-defaults`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('SaaS placeholders seeded');
      await fetchOverview();
    } catch (e) {
      toast.error('Seed gagal: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSeeding(false);
    }
  };

  return (
    <ResponsiveLayout>
      <div className="flex-1 min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] p-4 lg:p-6" data-testid="ua-dashboard-page">
        <SectionHeader
          icon={Sparkles}
          title="UA Dashboard"
          subtitle="Underads Capital — performance keseluruhan group merentas projek"
          iconBg="bg-gradient-to-br from-[#10B981] to-[#3B82F6] shadow-[#10B981]/20"
          action={
            <div className="flex items-center gap-2">
              <PeriodSelector value={period} onChange={setPeriod} available={data?.available_periods || []} />
              <Button onClick={() => fetchOverview(period)} disabled={loading} size="sm" variant="outline"
                className="h-9 border-[hsl(var(--border))] hover:border-[hsl(var(--border-hover))]"
                data-testid="refresh-ua-dashboard-btn">
                <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />Refresh
              </Button>
            </div>
          }
        />

        <div className="mt-5 space-y-6 max-w-[1400px]">
          {loading && !data && (
            <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-8 text-center">
              <RefreshCw size={20} className="animate-spin mx-auto text-[#6B7280] mb-2" />
              <p className="text-sm text-[#9CA3AF]">Loading UA overview...</p>
            </div>
          )}

          {data?.error === 'no_snapshot' && (
            <div className="bg-[#F59E0B]/5 border border-[#F59E0B]/20 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-[#F59E0B] flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <h3 className="text-white font-semibold mb-1">Scorecard belum di-sync</h3>
                  <p className="text-sm text-[#9CA3AF]">Admin Panel → Scorecard → Sync data dulu.</p>
                </div>
              </div>
            </div>
          )}

          {data?.success && (
            <>
              {/* COMBINED REVENUE HERO */}
              {data.company?.combined_revenue !== undefined && (
                <div className="relative bg-gradient-to-br from-[#10B981]/10 via-[hsl(var(--surface))] to-[#3B82F6]/10 border border-[#10B981]/30 rounded-2xl p-6 overflow-hidden" data-testid="ua-combined-revenue">
                  <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl bg-[#10B981]/20" />
                  <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full blur-3xl bg-[#3B82F6]/15" />
                  <div className="relative">
                    <div className="flex items-center gap-2.5 mb-2">
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#10B981] to-[#3B82F6] flex items-center justify-center shadow-[0_4px_20px_rgba(16,185,129,0.4)]">
                        <TrendingUp size={16} className="text-white" />
                      </div>
                      <div>
                        <p className="text-[10px] text-[#9CA3AF] uppercase tracking-[0.1em] font-medium">Combined Revenue · {data.period}</p>
                        <p className="text-[10px] text-[#6B7280]">Gabungan {(data.divisions || []).reduce((s, d) => s + (d.linked_channel_count || 0), 0)} active channels · 3 divisions</p>
                      </div>
                    </div>
                    <div className="flex items-end gap-4 flex-wrap">
                      <p className="text-5xl font-bold text-white tabular-nums tracking-tight">
                        <span className="text-2xl text-[#10B981] font-semibold mr-1">RM</span>
                        {Number(data.company.combined_revenue || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </p>
                      <div className="grid grid-cols-3 gap-2 flex-1 min-w-[280px]">
                        {(data.divisions || []).map(div => {
                          const pct = data.company.combined_revenue > 0 ? (div.revenue_total / data.company.combined_revenue * 100) : 0;
                          return (
                            <div key={div.pm_node_id} className="bg-[hsl(var(--surface))]/50 backdrop-blur-sm rounded-lg p-2.5 border border-[hsl(var(--border))]" data-testid={`combined-rev-div-${div.pm_node_id}`}>
                              <p className="text-[9px] text-[#6B7280] uppercase truncate">{div.name}</p>
                              <p className="text-sm font-bold text-white tabular-nums">RM {Number(div.revenue_total || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                              <p className="text-[10px] text-[#10B981]">{pct.toFixed(1)}%</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* COMPANY OVERALL */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <OverallSummaryCard
                    overall={data.company?.overall}
                    label={`Underads Capital — ${data.period}`}
                  />
                </div>
                <div className="md:col-span-1">
                  <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-5 h-full">
                    <div className="flex items-center gap-2 mb-3">
                      <TrendingUp size={14} className="text-[#10B981]" />
                      <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">6-month Trend</p>
                    </div>
                    {trend.length > 0 ? (
                      <>
                        <Sparkline data={trend} width={220} height={36} />
                        <p className="text-[9px] text-[#6B7280] mt-1">{trend[0]?.period} → {trend[trend.length - 1]?.period}</p>
                      </>
                    ) : (
                      <p className="text-xs text-[#6B7280]">No trend data</p>
                    )}
                    <div className="mt-4 pt-4 border-t border-[hsl(var(--border))] space-y-2">
                      <button onClick={() => navigate('/ib-management/dashboard')}
                        className="w-full flex items-center justify-between bg-[hsl(var(--surface-elevated))] hover:bg-[#10B981]/10 hover:border-[#10B981]/30 border border-transparent rounded-md px-3 py-2 transition group">
                        <span className="text-sm text-white">IB Management Dashboard</span>
                        <ChevronRight size={14} className="text-[#6B7280] group-hover:text-[#10B981]" />
                      </button>
                      <button onClick={() => navigate('/my-scorecard')}
                        className="w-full flex items-center justify-between bg-[hsl(var(--surface-elevated))] hover:bg-[#3B82F6]/10 hover:border-[#3B82F6]/30 border border-transparent rounded-md px-3 py-2 transition group">
                        <span className="text-sm text-white">My Personal Scorecard</span>
                        <ChevronRight size={14} className="text-[#6B7280] group-hover:text-[#3B82F6]" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* DIVISIONS (PMs) */}
              {data.divisions && data.divisions.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 size={16} className="text-[#9CA3AF]" />
                    <h2 className="text-white text-base font-semibold">Divisions</h2>
                    <span className="text-xs text-[#6B7280]">({data.divisions.length})</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {data.divisions.map(d => (
                      <DivisionCard
                        key={d.pm_node_id}
                        division={d}
                        trend={trend}
                        onClick={() => {
                          // Drill down: if it's the "Introducing Broker" division, go to IB Management
                          if ((d.name || '').toLowerCase().includes('broker') || (d.name || '').toLowerCase().includes('ib')) {
                            navigate('/ib-management/dashboard');
                          }
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* SaaS PRODUCTS */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Package size={16} className="text-[#9CA3AF]" />
                    <h2 className="text-white text-base font-semibold">SaaS Products</h2>
                    <span className="text-xs text-[#6B7280]">({(data.saas_products || []).length})</span>
                  </div>
                  {(data.saas_products || []).length === 0 && (
                    <Button onClick={seedSaas} disabled={seeding} size="sm"
                      data-testid="seed-saas-btn"
                      className="bg-gradient-to-r from-[#10B981] to-[#3B82F6] text-white text-xs h-8">
                      {seeding ? 'Seeding...' : 'Seed 5 Default Products'}
                    </Button>
                  )}
                </div>
                {(data.saas_products || []).length === 0 ? (
                  <div className="bg-[hsl(var(--surface))] border border-dashed border-[hsl(var(--border))] rounded-xl p-8 text-center">
                    <Package size={28} className="text-[#6B7280] mx-auto mb-2" />
                    <p className="text-sm text-[#9CA3AF] mb-1">SaaS placeholder cards belum di-seed.</p>
                    <p className="text-xs text-[#6B7280]">Click button di atas untuk seed default 5 (Lotifys, Threadly, Funnely, Tenderflix, Pantass).</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                    {data.saas_products.map(p => (
                      <SaaSCard key={p.id} product={p} onEdit={setEditingSaas} />
                    ))}
                  </div>
                )}
              </div>

              {/* Sembang Oh Yeah card */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <MessageCircle size={16} className="text-[#9CA3AF]" />
                  <h2 className="text-white text-base font-semibold">Community Platform</h2>
                </div>
                <div className="bg-gradient-to-br from-[#8B5CF6]/10 to-[#3B82F6]/5 border border-[#8B5CF6]/20 rounded-xl p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-lg bg-[#8B5CF6]/20 flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="text-[#8B5CF6]" size={22} />
                    </div>
                    <div className="flex-1">
                      <p className="text-white text-base font-semibold">Sembang Oh Yeah</p>
                      <p className="text-xs text-[#9CA3AF] mt-1">Community engagement platform. Integration tba.</p>
                      <p className="text-[10px] text-[#6B7280] mt-2 italic">Placeholder — metrics akan masuk apabila platform di-connect.</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <SaaSEditModal
          product={editingSaas}
          isOpen={!!editingSaas}
          onClose={() => setEditingSaas(null)}
          onSaved={() => fetchOverview(period)}
        />
      </div>
    </ResponsiveLayout>
  );
}
